import React, { FC, KeyboardEvent } from 'react';

import styled from 'styled-components';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useResetPasswordForm } from '@aqx-core/common/hooks/form/use-reset-password-form';
import { useCompanyName } from '@aqx-core/common/services/company';
import { useResetPasswordExperience } from '@aqx-core/common/services/reset-password-experience';
import { getEnterKeycode } from '@aqx-core/common/utils/keyboard';

import AuthAcknowledgementText from '../../shared/acknowledgement-text';
import AuthFormSecondaryAction from '../../shared/form-secondary-action';

const ResetPasswordForm: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const companyName = useCompanyName();
  const { loading } = useResetPasswordExperience();
  const { useFormMethods, submit, isDisabled, email } = useResetPasswordForm();
  const { formState, register } = useFormMethods;

  const {
    errors: { email: emailError }
  } = formState;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (getEnterKeycode(event)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <StyledContainer>
      <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Reset password')}</UIText>
      <StyledDescription typography={ETypography.TEXT_XL_REGULAR}>
        {t('Gain instant demo access with your email, and receive login details in your inbox.')}
      </StyledDescription>

      <FormProvider {...useFormMethods}>
        <StyledFormContainer onSubmit={submit}>
          <StyledFormInputsContainer>
            <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={'Email'} size="large">
              <UIInput
                {...register('email')}
                variant={EUIInputVariant.BORDERED_TEXT}
                placeholder={t('Enter your registered email')}
                value={email}
                error={emailError?.message}
                onKeyDown={handleKeyDown}
                dataTestId={EDataTestId.RESET_PASSWORD_EMAIL}
                size="large"
              />
            </UIFormItem>
          </StyledFormInputsContainer>
          <AuthAcknowledgementText
            prefixText={t(
              'By selecting Submit below, I consent to providing my email address to access the {{companyName}} Demo Platform and agree to {{companyName}}’s',
              { companyName }
            )}
          />
          <StyledButtonContainer>
            <UIButton
              size="large"
              stretch={true}
              loading={loading}
              disabled={isDisabled}
              variant={EUIButtonVariant.FILLED_THEMED}
              dataTestId={EDataTestId.RESET_PASSWORD_SUBMIT}
            >
              {t('Submit')}
            </UIButton>
          </StyledButtonContainer>
        </StyledFormContainer>
      </FormProvider>
      <AuthFormSecondaryAction
        actionText={t('Back to Log In')}
        onClickActionText={() => navigate(AUTHENTICATION_ROUTES.LOGIN)}
      />
    </StyledContainer>
  );
};

export default ResetPasswordForm;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 512px;
`;

const StyledFormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledDescription = styled(UIText)`
  margin-top: ${ESpacing.REGULAR}px;
  color: ${({ theme }) => theme.themeColors.text.subtle};
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
  margin-top: ${ESpacing.XXXL}px;
  margin-bottom: ${ESpacing.XL}px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  margin-top: ${ESpacing.XL}px;
  margin-bottom: ${ESpacing.M}px;
`;
